import { type ReactNode, type ComponentType, forwardRef } from 'react'
import { Badge, Card, Text, Heading } from '@agion/ui'
import { cn } from '@/core/infra_utils'
import type { LucideIcon } from 'lucide-react'
import type { AlertSeverity, HostStatus } from '@/core/infra_types'
import {
  type MonitoringTone,
  TONE_RING, TONE_TEXT, TONE_GLYPH, TONE_RIBBON,
  SEVERITY_TONE, HOST_STATUS_TONE,
} from './monitoring_tokens'

type IconLike = LucideIcon | ComponentType<{ className?: string; size?: number }>

export interface SummaryTileProps {
  label: string
  value: ReactNode
  tone?: MonitoringTone
  icon?: IconLike
  caption?: ReactNode
  valueTestId?: string
  className?: string
}

export const SummaryTile = forwardRef<HTMLDivElement, SummaryTileProps>(
  ({ label, value, tone = 'neutral', icon: Icon, caption, valueTestId, className }, ref) => (
    <Card
      ref={ref}
      className={cn(
        'relative overflow-hidden ring-1 ring-inset transition-colors',
        TONE_RING[tone],
        className,
      )}
    >
      <div className="p-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Text size="xs" color="muted" className="uppercase tracking-wider font-medium">
            {label}
          </Text>
          <div
            className={cn('mt-1.5 text-3xl font-semibold tabular-nums leading-none', TONE_TEXT[tone])}
            data-testid={valueTestId}
          >
            {value}
          </div>
          {caption && <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">{caption}</div>}
        </div>
        {Icon && (
          <div
            className={cn(
              'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg',
              tone === 'neutral'
                ? 'bg-slate-100 dark:bg-slate-800'
                : 'bg-current/10 dark:bg-current/15',
              TONE_GLYPH[tone],
            )}
          >
            <Icon className="h-5 w-5" />
          </div>
        )}
      </div>
    </Card>
  ),
)
SummaryTile.displayName = 'SummaryTile'

export interface SeverityBadgeProps {
  severity: AlertSeverity
  label: string
  ariaLabel: string
  icon?: ComponentType<{ size?: number; className?: string }>
  className?: string
}

export function SeverityBadge({ severity, label, ariaLabel, icon: Icon, className }: SeverityBadgeProps) {
  const variantBySeverity: Record<AlertSeverity, 'error' | 'warning' | 'info'> = {
    critical: 'error',
    warning:  'warning',
    info:     'info',
  }
  return (
    <Badge
      variant={variantBySeverity[severity]}
      role="status"
      aria-label={ariaLabel}
      className={cn('inline-flex items-center gap-1 capitalize', className)}
    >
      {Icon && <Icon size={12} aria-hidden="true" />}
      {label}
    </Badge>
  )
}

export interface HostStatusBadgeProps {
  status: HostStatus
  label: string
  className?: string
}

export function HostStatusBadge({ status, label, className }: HostStatusBadgeProps) {
  const tone = HOST_STATUS_TONE[status]
  const variantByTone: Record<MonitoringTone, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    neutral:  'default',
    success:  'success',
    warning:  'warning',
    critical: 'error',
    info:     'info',
  }
  return (
    <Badge
      variant={variantByTone[tone]}
      className={cn('inline-flex items-center gap-1.5 capitalize', className)}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          tone === 'success' ? 'bg-emerald-500' : tone === 'warning' ? 'bg-amber-500' : 'bg-rose-500',
        )}
      />
      {label}
    </Badge>
  )
}

export interface SeverityRibbonProps {
  severity: AlertSeverity
  className?: string
  children?: ReactNode
}

/**
 * Card with a 3px left-border ribbon coloured by severity.
 */
export function SeverityRibbon({ severity, className, children }: SeverityRibbonProps) {
  const tone = SEVERITY_TONE[severity]
  return (
    <Card className={cn('relative overflow-hidden', className)}>
      <span
        aria-hidden="true"
        className={cn('absolute left-0 top-0 h-full w-[3px]', TONE_RIBBON[tone])}
      />
      {children}
    </Card>
  )
}

export interface FilterShelfProps {
  className?: string
  children?: ReactNode
}

export function FilterShelf({ className, children }: FilterShelfProps) {
  return (
    <div
      className={cn(
        'mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200/70 bg-slate-50/50 p-3 dark:border-slate-800 dark:bg-slate-900/40',
        className,
      )}
    >
      {children}
    </div>
  )
}

export interface SectionTitleProps {
  className?: string
  children: ReactNode
  hint?: ReactNode
}

export function SectionTitle({ className, children, hint }: SectionTitleProps) {
  return (
    <div className={cn('mb-3 flex items-baseline justify-between gap-3', className)}>
      <Heading level="h3" className="text-base font-semibold">
        {children}
      </Heading>
      {hint}
    </div>
  )
}
